// Duration-honoring key highlighting. A pure, stateless per-frame state machine: a key's
// colour derives from the song notes whose span touches "now" and whether the key is held —
// no cross-frame memory. As-built → docs/architecture.md → "Play modes" → key highlighting.
import type { KeyState } from "../types";

export interface KeyHighlightCfg {
  /** Blue anticipation lead before a note's onset (s). */
  leadSec: number;
  /** Timing window each side of the onset (s) — the "play now" zone (= the hit window). */
  windowSec: number;
  /** A release within this of the note end isn't "early" (s). */
  releaseMarginSec: number;
}

/**
 * The display state of ONE note's key at playhead `t` (null = white / not applicable).
 *
 * approaching   `[O−lead, O−W)`        not held → upcoming   · held → wrong (early press)
 * play-now      `[O−W, O+W]`           not held → expected   · held → correct
 * must-hold     `(O+W, O+D−rel)`       not held → wrong      · held → correct
 * release tail  `[O+D−rel, O+D)`       not held → null       · held → correct
 *
 * The three recoveries (late press red→green, early-press correction red→green, early
 * release green→red) fall out as `held` changes frame to frame — no state is kept.
 */
export function keyHighlightState(
  t: number,
  onset: number,
  duration: number,
  held: boolean,
  cfg: KeyHighlightCfg,
): KeyState | null {
  if (t < onset - cfg.leadSec || t >= onset + duration) return null;
  if (t < onset - cfg.windowSec) return held ? "wrong" : "upcoming"; // approaching
  if (t <= onset + cfg.windowSec) return held ? "correct" : "expected"; // play-now window
  if (held) return "correct"; // past the window but held → correct (sustained / late grab)
  return t < onset + duration - cfg.releaseMarginSec ? "wrong" : null; // unheld: missed vs release-OK
}

// Precedence when several active notes touch one key: held-green wins, then the most
// actionable cue (play-now ▸ something-wrong ▸ approaching).
const RANK: Record<KeyState, number> = { correct: 3, expected: 2, wrong: 1, upcoming: 0 };

export function mergeKeyState(a: KeyState, b: KeyState): KeyState {
  return RANK[b] > RANK[a] ? b : a;
}

/** Per-key states for one frame: run the machine over each active note, merge by pitch,
 *  then any held pitch with no active note is a genuine wrong note. */
export function buildKeyStates(
  activeNotes: readonly { midi: number; onset: number; duration: number }[],
  playhead: number,
  held: ReadonlySet<number>,
  cfg: KeyHighlightCfg,
): Map<number, KeyState> {
  const states = new Map<number, KeyState>();
  for (const n of activeNotes) {
    const s = keyHighlightState(playhead, n.onset, n.duration, held.has(n.midi), cfg);
    if (s === null) continue;
    const prev = states.get(n.midi);
    states.set(n.midi, prev ? mergeKeyState(prev, s) : s);
  }
  for (const pitch of held) if (!states.has(pitch)) states.set(pitch, "wrong");
  return states;
}
